import React, { useState, useEffect } from 'react';
import useAuthStore from '../../store/authStore';
import { useSearchParams } from 'react-router-dom';
import api from '../../api/axiosInstance';
import { toast } from 'react-toastify';
import {
  UserCircleIcon,
  ShieldCheckIcon,
  EyeIcon,
  EyeSlashIcon,
  Bars3Icon,
} from '../../lib/commonUtils';
import { useLanguage } from '../../context/LanguageContext';

const MIN_PASS_LEN = 8;

const Profile = () => {
  const { t } = useLanguage();
  const { user, token, getProfile, updatePassword } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  useEffect(() => {
    setActiveTab(searchParams.get('tab') || 'profile');
  }, [searchParams]);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone_number || user.phone || '',
        company: user.company_name || '',
      });
    }
  }, [user]);

  useEffect(() => {
    getProfile?.();
  }, [getProfile]);

  const setTab = (tab) => {
    setActiveTab(tab);
    setMobileMenuOpen(false);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };

  const handleChange = (field) => (e) => {
    setProfileData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handlePasswordFieldChange = (field) => (e) => {
    setPasswordData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const initials =
    (profileData.name || '')
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((n) => n[0]?.toUpperCase())
      .join('') || 'U';

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    if (savingProfile) return;
    setSavingProfile(true);
    try {
      const payload = {};
      if (profileData.name) payload.full_name = profileData.name;
      if (profileData.email) payload.email = profileData.email;
      if (profileData.phone) payload.phone = profileData.phone;
      await api.patch('/auth/profile', payload);
      await getProfile?.();
      toast.success(t('profileUpdated'));
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        t('failedUpdateProfile');
      toast.error(msg);
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (savingPassword) return;

    const current = passwordData.currentPassword?.trim();
    const next = passwordData.newPassword?.trim();
    const confirm = passwordData.confirmPassword?.trim();

    if (!current || !next || !confirm) {
      toast.error(t('fillAllPassword'));
      return;
    }
    if (next.length < MIN_PASS_LEN) {
      toast.error(t('passTooShort').replace('{min}', MIN_PASS_LEN));
      return;
    }
    if (next !== confirm) {
      toast.error(t('passMismatch'));
      return;
    }
    if (current === next) {
      toast.error(t('passSameAsCurrent'));
      return;
    }

    setSavingPassword(true);
    try {
      const res = await updatePassword?.({
        current_password: current,
        new_password: next,
        confirm_password: confirm,
      });

      if (res?.success) {
        toast.success(t('passUpdateSuccess'));
        setShowPasswordChange(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        toast.error(res?.error || t('failedUpdatePass'));
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        t('failedUpdatePass');
      toast.error(msg);
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-2xl sm:text-2xl font-bold text-white tracking-tight">{t('profile')}</h1>
        <p className="mt-2 sm:mt-1 text-sm sm:text-xs font-medium text-white/40 italic">{t('profileDesc')}</p>
      </div>

      <div className="md:hidden">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="w-full flex items-center justify-between p-4 controlled-glass rounded-xl inner-highlight"
        >
          <span className="font-bold text-white tracking-tight">
            {activeTab === 'profile' ? t('profileInfo') : t('security')}
          </span>
          <Bars3Icon className="h-5 w-5 text-white/40" />
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden controlled-glass rounded-xl shadow-2xl mt-2 inner-highlight overflow-hidden">
          <nav className="flex flex-col p-2">
            <button
              onClick={() => setTab('profile')}
              className={`py-3 px-4 text-left rounded-lg font-bold text-xs uppercase tracking-widest ${activeTab === 'profile'
                ? 'bg-primary-500/10 text-primary-400 border border-primary-500/20'
                : 'text-white/40 hover:bg-white/5'
                }`}
            >
              <UserCircleIcon className="h-4 w-4 inline-block mr-2" />
              {t('profileInfo')}
            </button>
            <button
              onClick={() => setTab('security')}
              className={`py-3 px-4 text-left rounded-lg font-bold text-xs uppercase tracking-widest ${activeTab === 'security'
                ? 'bg-primary-500/10 text-primary-400 border border-primary-500/20'
                : 'text-white/40 hover:bg-white/5'
                }`}
            >
              <ShieldCheckIcon className="h-4 w-4 inline-block mr-2" />
              {t('security')}
            </button>
          </nav>
        </div>
      )}

      <div className="hidden md:block border-b border-white/5">
        <nav className="-mb-px flex space-x-8 sm:space-x-6">
          <button
            onClick={() => setTab('profile')}
            className={`py-4 sm:py-3 px-1 border-b-2 font-bold text-[10px] sm:text-[9px] uppercase tracking-widest transition-all ${activeTab === 'profile'
              ? 'border-primary-500 text-primary-400'
              : 'border-transparent text-white/40 hover:text-white hover:border-white/20'
              }`}
          >
            <UserCircleIcon className="h-4 w-4 sm:h-3.5 sm:w-3.5 inline-block mr-2 sm:mr-1.5" />
            {t('profileInfo')}
          </button>
          <button
            onClick={() => setTab('security')}
            className={`py-4 sm:py-3 px-1 border-b-2 font-bold text-[10px] sm:text-[9px] uppercase tracking-widest transition-all ${activeTab === 'security'
              ? 'border-primary-500 text-primary-400'
              : 'border-transparent text-white/40 hover:text-white hover:border-white/20'
              }`}
          >
            <ShieldCheckIcon className="h-4 w-4 sm:h-3.5 sm:w-3.5 inline-block mr-2 sm:mr-1.5" />
            {t('security')}
          </button>
        </nav>
      </div>

      <div className="card">
        {activeTab === 'profile' && (
          <form onSubmit={handleProfileUpdate} className="space-y-6 sm:space-y-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-4">
              <div className="w-20 h-20 sm:w-20 sm:h-20 bg-primary-500 rounded-full flex items-center justify-center shadow-lg shadow-primary-500/20 border border-white/20">
                <span className="text-xl sm:text-xl font-bold text-white">{initials}</span>
              </div>
              <div>
                <h3 className="text-xl sm:text-lg font-bold text-white tracking-tight">{profileData.name || t('yourName')}</h3>
                <p className="text-sm sm:text-xs font-medium text-white/40 italic">{profileData.company || t('yourCompanyName')}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:gap-4 sm:grid-cols-2">
              <div>
                <label className="text-[10px] sm:text-[9px] font-bold text-white/30 uppercase tracking-widest pl-1 mb-2 sm:mb-1 block">{t('fullName')}</label>
                <input
                  type="text"
                  className="input text-base sm:text-sm"
                  value={profileData.name}
                  onChange={handleChange('name')}
                  placeholder={t('enterFullName')}
                />
              </div>
              <div>
                <label className="text-[10px] sm:text-[9px] font-bold text-white/30 uppercase tracking-widest pl-1 mb-2 sm:mb-1 block">{t('email')}</label>
                <input
                  type="email"
                  className="input text-base sm:text-sm"
                  value={profileData.email}
                  onChange={handleChange('email')}
                  placeholder={t('enterEmail')}
                />
              </div>
              <div>
                <label className="text-[10px] sm:text-[9px] font-bold text-white/30 uppercase tracking-widest pl-1 mb-2 sm:mb-1 block">{t('phoneLabel')}</label>
                <input
                  type="tel"
                  className="input text-base sm:text-sm"
                  value={profileData.phone}
                  onChange={handleChange('phone')}
                  placeholder={t('enterPhone')}
                />
              </div>
              <div>
                <label className="text-[10px] sm:text-[9px] font-bold text-white/30 uppercase tracking-widest pl-1 mb-2 sm:mb-1 block">{t('companyLabel')}</label>
                <input
                  type="text"
                  className="input text-base sm:text-sm"
                  value={profileData.company}
                  onChange={handleChange('company')}
                  placeholder={t('companyPlaceholder')}
                />
              </div>
            </div>

            <div className="flex justify-end pt-6 sm:pt-4 border-t border-white/5">
              <button type="submit" className="btn btn-primary w-full sm:w-auto sm:px-10" disabled={savingProfile}>
                {savingProfile ? t('saving') : t('saveChanges')}
              </button>
            </div>
          </form>
        )}

        {activeTab === 'security' && (
          <div className="space-y-6 sm:space-y-5">
            <div className="controlled-glass p-8 sm:p-5 rounded-[32px] sm:rounded-2xl inner-highlight">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 sm:mb-5 gap-4">
                <div>
                  <h3 className="text-xl sm:text-lg font-bold text-white tracking-tight">{t('passwordLabel')}</h3>
                  <p className="text-sm sm:text-xs font-medium text-white/40 italic">{t('passwordDescLabel')}</p>
                </div>
                <button
                  onClick={() => setShowPasswordChange(!showPasswordChange)}
                  className="btn btn-secondary"
                >
                  {t('changePassword')}
                </button>
              </div>

              {showPasswordChange && (
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div className="relative group">
                    <input
                      type={showCurrent ? 'text' : 'password'}
                      className="input pr-12 text-base sm:text-sm"
                      placeholder={t('currentPassword')}
                      value={passwordData.currentPassword}
                      onChange={handlePasswordFieldChange('currentPassword')}
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white/20 hover:text-white transition-colors"
                      onClick={() => setShowCurrent(!showCurrent)}
                    >
                      {showCurrent ? <EyeSlashIcon className="h-5 w-5 sm:h-4 w-4" /> : <EyeIcon className="h-5 w-5 sm:h-4 w-4" />}
                    </button>
                  </div>

                  <div className="relative group">
                    <input
                      type={showNew ? 'text' : 'password'}
                      className="input pr-12 text-base sm:text-sm"
                      placeholder={t('newPassword')}
                      value={passwordData.newPassword}
                      onChange={handlePasswordFieldChange('newPassword')}
                      required
                      minLength={MIN_PASS_LEN}
                    />
                    <button
                      type="button"
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white/20 hover:text-white transition-colors"
                      onClick={() => setShowNew(!showNew)}
                    >
                      {showNew ? <EyeSlashIcon className="h-5 w-5 sm:h-4 w-4" /> : <EyeIcon className="h-5 w-5 sm:h-4 w-4" />}
                    </button>
                  </div>

                  <div className="relative group">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      className="input pr-12 text-base sm:text-sm"
                      placeholder={t('confirmNewPassword')}
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordFieldChange('confirmPassword')}
                      required
                      minLength={MIN_PASS_LEN}
                    />
                    <button
                      type="button"
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white/20 hover:text-white transition-colors"
                      onClick={() => setShowConfirm(!showConfirm)}
                    >
                      {showConfirm ? <EyeSlashIcon className="h-5 w-5 sm:h-4 w-4" /> : <EyeIcon className="h-5 w-5 sm:h-4 w-4" />}
                    </button>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 pt-4 sm:pt-2">
                    <button type="submit" className="btn btn-primary w-full sm:w-auto sm:px-10" disabled={savingPassword}>
                      {savingPassword ? t('updating') : t('updatePassword')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary w-full sm:w-auto"
                      onClick={() => setShowPasswordChange(false)}
                      disabled={savingPassword}
                    >
                      {t('cancel')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;